import { hydrateMissions } from "./missionHydrate";
import { hydrateCareer } from "./career";
import { liveCompanyInbox } from "./companySync";
import { hydrateCompany } from "./company";
import { reconcileInForceDuty } from "./ftlScheme";
import { hydrateCrewRoster } from "./crew";
import { emptyDutyRoster, hydrateDutyRoster } from "./duty";
import {
  catalogsAsStubs,
  catalogsFromUnknown,
  hydrateMoneyCatalogs,
  icaosFromScenery,
  type MoneyCatalogs,
} from "./catalogs";
import { defaultAccount, hydrateAccount, hydrateRates } from "./ledger";
import { hydrateLogbook } from "./logbook";
import { hydrateMelItems } from "./mel";
import { hydrateFleet, refreshFleetStatus } from "./maintenance";
import { DEFAULT_PILOT, normalizeRatedTypes } from "./profile";
import { seedFleet, starterScenery } from "./airports";
import { liveFleetFromPersist } from "./fleetBook";
import type {
  AircraftSpec,
  CareerState,
  CharterRequest,
  CompanyAccount,
  CompanyProfile,
  CrewMember,
  DutyStandbyState,
  FlightLogEntry,
  MelItem,
  Mission,
  PilotLogbookEntry,
  PilotProfile,
  RateTables,
  SceneryAirport,
  Squawk,
  ThemeAccentId,
  ThemeId,
  ThemePackId,
} from "./types";
import type { DutyRoster } from "./duty";
import { parseSnapshot, type SyncSnapshot } from "./sync";
import { hydrateDutyStandby } from "./voiceDesk";
import { emptyDutyStandby } from "./dutyStandby";
import { hydrateInboxList } from "./voiceInbox";
import { hydrateFlightTracks, type FlightTrack } from "./aar";
import { hydrateFlightLogs } from "./flightlog";
import { hydrateJobMode, type JobMode } from "./jobMode";
import {
  hydrateAssignmentAudit,
  hydrateDeclineCooldowns,
  hydrateTripAssignments,
  type AssignmentAuditEvent,
  type DeclineCooldown,
  type TripAssignment,
} from "./assignment";
import { hydrateFusedPosition, type FusedPosition } from "./position";
import { hydrateSopOverrides, type SopOverrides } from "./sops";
import { hydrateAutoInboxSettings, type AutoInboxSettings } from "./autoInbox";
import { hydrateDispatchPlan, type DispatchPlan } from "./dispatchBrain";
import {
  hydrateDispatcherBrain,
  resolveDispatcherBrainOnLoad,
  hydrateGrokBrainCheckpoint,
  hydrateGrokBrainReviews,
  type DispatcherBrainMode,
  type GrokBrainCheckpoint,
  type GrokBrainReview,
} from "./grokDispatchBrain";
import { hydrateWeightBalanceDesk, type WeightBalanceDesk } from "./weightBalance";
import { deskChromeFromUnknown, type DeskChrome } from "./themePacks";

export const CAREER_SAVE_KIND = "northstar.dispatch.career";
export const CAREER_SAVE_VERSION = 1 as const;

export const CAREER_CACHE_KEY = "ns.dispatch.career.cache";

export type CareerSavePayload = {
  company: CompanyProfile;
  /** Day / night — also on company + deskChrome. Nanode career is account-scoped. */
  theme?: ThemeId;
  themePack?: ThemePackId;
  themeAccent?: ThemeAccentId;
  deskChrome?: DeskChrome;
  crew: CrewMember[];
  profile: PilotProfile;
  account: CompanyAccount;
  fleet: AircraftSpec[];
  scenery: SceneryAirport[];
  missions: Mission[];
  inbox: CharterRequest[];
  flightLogs: FlightLogEntry[];
  rates: RateTables;
  squawks: Squawk[];
  melItems: MelItem[];
  logbook: PilotLogbookEntry[];
  duty: DutyRoster;
  career: CareerState;
  catalogStubs: Record<string, unknown>;
  catalogs?: MoneyCatalogs;
  dutyStandby?: DutyStandbyState;
  jobMode?: JobMode;
  assignments?: TripAssignment[];
  assignmentAudit?: AssignmentAuditEvent[];
  declineCooldowns?: DeclineCooldown[];
  lastKnownPosition?: FusedPosition | null;
  flightTracks?: FlightTrack[];
  sopOverrides?: SopOverrides;
  weightBalance?: WeightBalanceDesk;
  autoInbox?: AutoInboxSettings;
  dispatchPlan?: DispatchPlan | null;
  dispatcherBrain?: DispatcherBrainMode;
  dispatcherBrainUserSet?: boolean;
  grokBrainCheckpoint?: GrokBrainCheckpoint | null;
  brainReviews?: GrokBrainReview[];
};

export type CareerSaveBlob = {
  kind: typeof CAREER_SAVE_KIND;
  version: typeof CAREER_SAVE_VERSION;
  savedAtUtc: string;
  payload: CareerSavePayload;
};

export type CareerSaveSource = CareerSavePayload;

export type ParseCareerSaveResult = { ok: true; blob: CareerSaveBlob } | { ok: false; error: string };

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function hydrateProfile(raw?: Partial<PilotProfile>): PilotProfile {
  const name = raw?.name && raw.name !== "Desk" ? raw.name : DEFAULT_PILOT.name;
  return {
    ...DEFAULT_PILOT,
    ...raw,
    name,
    ratedIcaoTypes: normalizeRatedTypes(raw?.ratedIcaoTypes),
  };
}

export function hydrateCareerSavePayload(raw: Partial<CareerSavePayload>): CareerSavePayload {
  const squawks = Array.isArray(raw.squawks) ? raw.squawks : [];
  const melItems = hydrateMelItems(raw.melItems);
  const logbook = hydrateLogbook(raw.logbook);
  const fleet = refreshFleetStatus(
    Array.isArray(raw.fleet) ? liveFleetFromPersist(raw.fleet) : seedFleet(),
    squawks,
    melItems,
  );
  const chrome = deskChromeFromUnknown(raw);
  const company = hydrateCompany(
    { ...raw.company, ...chrome },
    { legacyPersist: Boolean(raw.company) },
  );
  const profile = hydrateProfile(raw.profile);
  const catalogs = hydrateMoneyCatalogs(raw.catalogs ?? catalogsFromUnknown(raw.catalogStubs ?? raw.career?.catalogStubs), {
    icaos: icaosFromScenery(raw.scenery),
    rates: hydrateRates(raw.rates),
  });
  const saved = {
    company,
    theme: chrome.theme,
    themePack: chrome.themePack,
    themeAccent: chrome.themeAccent,
    deskChrome: chrome,
    crew: hydrateCrewRoster(raw.crew, profile, { seedIfEmpty: Boolean(company.setupComplete) }),
    profile,
    account: raw.account ? hydrateAccount(raw.account) : defaultAccount(),
    fleet,
    scenery: Array.isArray(raw.scenery) ? raw.scenery : starterScenery(),
    missions: hydrateMissions(raw.missions),
    inbox: hydrateInboxList(raw.inbox),
    flightLogs: hydrateFlightLogs(raw.flightLogs),
    rates: hydrateRates(raw.rates),
    squawks,
    melItems,
    logbook,
    duty: raw.duty ? hydrateDutyRoster(raw.duty) : emptyDutyRoster(),
    career: hydrateCareer(
      {
        ...raw.career,
        catalogStubs: catalogsAsStubs(catalogs),
      },
      { companyConfigured: Boolean(company.setupComplete) },
    ),
    catalogStubs: catalogsAsStubs(catalogs),
    catalogs,
    dutyStandby: raw.dutyStandby ? hydrateDutyStandby(raw.dutyStandby) : emptyDutyStandby(),
    jobMode: hydrateJobMode(raw.jobMode),
    assignments: hydrateTripAssignments(raw.assignments),
    assignmentAudit: hydrateAssignmentAudit(raw.assignmentAudit),
    declineCooldowns: hydrateDeclineCooldowns(raw.declineCooldowns),
    lastKnownPosition: hydrateFusedPosition(raw.lastKnownPosition),
    flightTracks: hydrateFlightTracks(raw.flightTracks),
    sopOverrides: hydrateSopOverrides(raw.sopOverrides),
    weightBalance: hydrateWeightBalanceDesk(raw.weightBalance),
    autoInbox: hydrateAutoInboxSettings(raw.autoInbox),
    dispatchPlan: hydrateDispatchPlan(raw.dispatchPlan),
    ...(() => {
      const resolved = resolveDispatcherBrainOnLoad({
        raw: raw.dispatcherBrain,
        userSet: raw.dispatcherBrainUserSet,
        hasCheckpoint: Boolean(raw.grokBrainCheckpoint),
      });
      return { dispatcherBrain: resolved.mode, dispatcherBrainUserSet: resolved.userSet };
    })(),
    grokBrainCheckpoint: hydrateGrokBrainCheckpoint(raw.grokBrainCheckpoint),
    brainReviews: hydrateGrokBrainReviews(raw.brainReviews),
  };
  const aligned = reconcileInForceDuty(saved.company, saved.duty.settings);
  return {
    ...saved,
    company: aligned.company,
    duty: { ...saved.duty, settings: aligned.duty },
  };
}

export function buildCareerSave(source: CareerSaveSource, now = new Date()): CareerSaveBlob {
  const payload = hydrateCareerSavePayload(source);
  return {
    kind: CAREER_SAVE_KIND,
    version: CAREER_SAVE_VERSION,
    savedAtUtc: now.toISOString(),
    payload: {
      ...payload,
      inbox: liveCompanyInbox(payload.inbox),
    },
  };
}

export function parseCareerSave(input: unknown): ParseCareerSaveResult {
  let value = input;
  if (typeof input === "string") {
    try {
      value = JSON.parse(input) as unknown;
    } catch {
      return { ok: false, error: "Career save is not valid JSON." };
    }
  }
  if (!isRecord(value)) return { ok: false, error: "Career save is not an object." };

  if (value.kind === CAREER_SAVE_KIND) {
    if (value.version !== CAREER_SAVE_VERSION) {
      return { ok: false, error: `Unsupported career save version (${String(value.version)}).` };
    }
    if (!isRecord(value.payload)) return { ok: false, error: "Career save is missing payload." };
    return {
      ok: true,
      blob: {
        kind: CAREER_SAVE_KIND,
        version: CAREER_SAVE_VERSION,
        savedAtUtc: typeof value.savedAtUtc === "string" ? value.savedAtUtc : new Date().toISOString(),
        payload: hydrateCareerSavePayload(value.payload as Partial<CareerSavePayload>),
      },
    };
  }

  const snap = parseSnapshot(value);
  if (snap.ok) {
    return { ok: true, blob: careerSaveFromSnapshot(snap.snapshot) };
  }
  return { ok: false, error: "File is not a Northstar career save or dispatch snapshot." };
}

export function careerSaveFromSnapshot(snapshot: SyncSnapshot): CareerSaveBlob {
  const p = snapshot.payload;
  return buildCareerSave(
    {
      company: p.company,
      theme: p.theme,
      themePack: p.themePack,
      themeAccent: p.themeAccent,
      deskChrome: p.deskChrome,
      crew: p.crew,
      profile: p.profile,
      account: p.account,
      fleet: p.fleet,
      scenery: p.scenery,
      missions: p.missions,
      inbox: p.inbox,
      flightLogs: p.flightLogs,
      rates: p.rates,
      squawks: p.squawks,
      melItems: p.melItems ?? [],
      logbook: p.logbook ?? [],
      duty: p.duty,
      career: p.career ?? hydrateCareer(undefined, { companyConfigured: Boolean(p.company?.setupComplete) }),
      catalogStubs: p.career?.catalogStubs ?? {},
      catalogs: p.catalogs,
      dutyStandby: p.dutyStandby,
      jobMode: p.jobMode,
      assignments: p.assignments,
      assignmentAudit: p.assignmentAudit,
      declineCooldowns: p.declineCooldowns,
      lastKnownPosition: p.lastKnownPosition,
      flightTracks: p.flightTracks,
      sopOverrides: p.sopOverrides,
      weightBalance: p.weightBalance,
      autoInbox: p.autoInbox,
      dispatchPlan: p.dispatchPlan,
      dispatcherBrain: p.dispatcherBrain,
      dispatcherBrainUserSet: p.dispatcherBrainUserSet,
      grokBrainCheckpoint: p.grokBrainCheckpoint,
      brainReviews: p.brainReviews,
    },
    snapshot.exportedAtUtc ? new Date(snapshot.exportedAtUtc) : new Date(),
  );
}

export function careerSaveSummary(blob: CareerSaveBlob): string {
  const p = blob.payload;
  return [
    `saved ${blob.savedAtUtc}`,
    p.career.playMode,
    p.company.operatorName,
    `${p.catalogs?.currency ?? "CAD"} ${p.account.balanceCad}`,
    `fleet ${p.fleet.length}`,
    `dest ${p.scenery.length}`,
  ].join(" · ");
}

export function loadCareerCache(storage: Pick<Storage, "getItem"> | null | undefined): ParseCareerSaveResult {
  if (!storage) return { ok: false, error: "No local cache." };
  const raw = storage.getItem(CAREER_CACHE_KEY);
  if (!raw) return { ok: false, error: "No local career cache." };
  return parseCareerSave(raw);
}

export function saveCareerCache(blob: CareerSaveBlob, storage: Pick<Storage, "setItem"> | null | undefined): void {
  if (!storage) return;
  storage.setItem(CAREER_CACHE_KEY, JSON.stringify(blob));
}
